import clsx from 'clsx';
import React, { createContext, Suspense, useContext, useState } from 'react';

/* eslint-disable @nx/enforce-module-boundaries */
import { Box, IconButton, List, ListItemButton, Tooltip } from '@mui/material';
import { useCurrentuser } from '@uplift/contexts';
import { trackVideoSidebarButtonClick } from '@uplift/hooks';
import { UserRole } from '@uplift/types';
import { AlertProvider, CollapseWidth, UpliftThemeProvider } from '@uplift/ui-v2';

import { useVideoCallLayoutStyles } from './styles';

const VideoCallSidebarContext = createContext<{
  activeItemId: string | null;
  setActiveItemId: (i: string) => void;
} | null>(null);

export const VideoCallSidebarProvider: React.FC = ({ children }) => {
  const [ activeItemId, setActiveItemId ] = useState<string | null>(null);

  return (
    <VideoCallSidebarContext.Provider
      value={{ activeItemId, setActiveItemId }}
      children={children}
    />
  );
};


export const VideoCallSidebarAlertProvider: React.FC<{ contentWidth: string }> = ({ children, contentWidth }) => {
  return (
    <AlertProvider
      containerStyle={{
        width: contentWidth,
        left: 'auto',
        right: '64px',
        marginTop: '16px',
      }}
    >
      {children}
    </AlertProvider>
  );
};


export const useVideoCallSidebarContext = () => {
  const context = useContext(VideoCallSidebarContext);

  if (context === null) {
    console.warn('"VideoCallSidebarProvider" wrapper is not found');
  }
  return context;
};


export type VideoCallSidebarItem = {
  id: string;
  tooltipTitle: string;
  renderContent: (onClose: () => void) => React.ReactNode;
  icon: React.ReactNode;
  contentWidth?: number;
  // If true the component will not unmount when de-selected ( this is useful for keeping state )
  keepMounted?: boolean;
  disabled?: boolean;
};

export type VideoCallSidebarProps = {
  sidebarItems: VideoCallSidebarItem[];
};

const defaultDrawerWidth = 420;
const drawerTimeout = 400;

export const VideoCallSidebar: React.VFC<VideoCallSidebarProps> = ({ sidebarItems }) => {
  const { classes } = useVideoCallLayoutStyles();
  const { activeItemId, setActiveItemId } = useVideoCallSidebarContext();
  const me = useCurrentuser();

  const onSidebarItemClick = (id: string, tooltipTitle: string) => {
    trackVideoSidebarButtonClick({
      buttonType: tooltipTitle,
      userType: UserRole[me?.role],
      medicalRole: me?.medicalRole
    });
    if (activeItemId) {
      setActiveItemId(null);
      if (activeItemId !== id) {
        setTimeout(() => setActiveItemId(id), drawerTimeout);
      }
    } else {
      setActiveItemId(id);
    }
  };

  const onCloseDrawer = () => setActiveItemId(null);


  return (
    <Box className={classes.sidebarContainer} ml={activeItemId ? 2 : 0.5}>
      <UpliftThemeProvider>
        {sidebarItems.map(({ id, contentWidth, renderContent, keepMounted }) => {
          const isActive = id === activeItemId;
          const shouldRenderContent = keepMounted || isActive;

          return (
            <CollapseWidth
              key={id}
              in={isActive}
              timeout={drawerTimeout}
              width={`${contentWidth ?? defaultDrawerWidth}px`}
            >
              <Box
                className={classes.sidebarDrawer}
                width={`${contentWidth ?? defaultDrawerWidth}px`}
              >
                {shouldRenderContent && (
                  <Suspense fallback={null}>
                    {renderContent(onCloseDrawer)}
                  </Suspense>
                )}
              </Box>
            </CollapseWidth>
          );
        })}

      </UpliftThemeProvider>
      <List disablePadding>
        {sidebarItems.map(({ id, icon, tooltipTitle, disabled }) => {
          const isActive = id === activeItemId;

          return (
            <ListItemButton
              key={id}
              disableGutters
              className={classes.sidebarItem}
              disabled={disabled}
            >
              <Tooltip
                disableHoverListener={disabled}
                aria-disabled={disabled}
                title={tooltipTitle}
                placement="left"
                className={classes.tooltip}
              >
                <IconButton
                  color="inherit"
                  className={clsx(classes.sidebarItemButton, {
                    [classes.activeSidebarItemButton]: isActive
                  })}
                  onClick={disabled ? undefined : () => onSidebarItemClick(id, tooltipTitle)}
                >
                  {icon}
                </IconButton>
              </Tooltip>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

